/**
 * GLOBO PRODUCT OPTIONS -> PRODUCT GALLERY
 *
 * Replaces the main product gallery by the images of the value selected in a Globo option (ex: the stone).
 *
 * The product metafield "custom.globo_visuels" is a list of metaobjects, each with:
 * - "reference": must be contained in the selected Globo value, ex: "Medium Blue" for "Saphir chauffé Medium Blue du Montana..."
 * - "images": the images replacing the whole gallery when this value is selected
 * The comparison ignores case, accents and apostrophes.
 *
 * product-gallery.liquid renders those images after the product media, hidden and tagged with "data-gpo-ref". When no
 * value is selected, or when the selected value has no images, the default product gallery is shown.
 */
(() => {
  const GALLERY_SELECTOR = 'product-gallery[form^="product-form-main-"]';

  const normalize = (text) => (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  // Globo does not send any detail with its events, the selected values are read from its hidden inputs
  const getGloboValues = () => {
    return Array.from(document.querySelectorAll('.gpo-hidden-inputs [data-field-name]'))
      .map((input) => normalize(input.getAttribute('data-value') || input.value))
      .filter(Boolean);
  };

  const filterGallery = (gallery, { focusSelected = false } = {}) => {
    const carousel = gallery.carousel;

    if (!carousel || !gallery.querySelector('[data-gpo-ref]')) {
      return;
    }

    // The theme rewrites "filtered-indexes" when the variant changes: we keep it as base for the default mode
    const currentAttribute = gallery.getAttribute('filtered-indexes');

    if (currentAttribute !== gallery.gpoWrittenIndexes) {
      gallery.gpoBaseIndexes = JSON.parse(currentAttribute || '[]').map((position) => parseInt(position));
    }

    const globoValues = getGloboValues();
    const cells = carousel.allCells;
    const matchingCells = cells.filter((cell) => {
      const reference = normalize(cell.getAttribute('data-gpo-ref'));
      return reference !== '' && globoValues.some((value) => value.includes(reference));
    });

    // Globo images of the selected value replace all the other media. Without any, the default product gallery (as
    // filtered by the theme) is shown.
    const hiddenPositions = new Set();

    cells.forEach((cell, index) => {
      const isHidden = matchingCells.length > 0
        ? !matchingCells.includes(cell)
        : cell.hasAttribute('data-gpo-ref') || gallery.gpoBaseIndexes.includes(index + 1);

      if (isHidden) {
        hiddenPositions.add(index + 1);
      }
    });

    const positions = Array.from(hiddenPositions).sort((a, b) => a - b);

    gallery.gpoWrittenIndexes = JSON.stringify(positions);
    gallery.setAttribute('filtered-indexes', gallery.gpoWrittenIndexes);

    if (!cells.some((cell, index) => cell.hasAttribute('hidden') !== hiddenPositions.has(index + 1))) {
      return;
    }

    const previousCell = carousel.selectedCell;

    carousel.filter(positions.map((position) => position - 1));

    // Keep the lightbox buttons (grid layouts) in sync with the visible images
    carousel.cells.filter((cell) => cell.getAttribute('data-media-type') === 'image').forEach((cell, imageIndex) => {
      cell.querySelectorAll('open-lightbox-button[image-index]').forEach((button) => button.setAttribute('image-index', imageIndex.toString()));
    });

    // Select the first Globo image, or go back to the previously selected / initial product media
    let targetCell = matchingCells[0] || previousCell;

    if (!targetCell || targetCell.hasAttribute('hidden')) {
      const initialCell = gallery.querySelector('.product-gallery__media.is-initial');
      targetCell = initialCell && !initialCell.hasAttribute('hidden') ? initialCell : carousel.cells[0];
    }

    if (carousel.isScrollable) {
      carousel.select(Math.max(carousel.cells.indexOf(targetCell), 0), { instant: !focusSelected });
    } else if (focusSelected && targetCell) {
      targetCell.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  const filterAllGalleries = (options) => {
    document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => filterGallery(gallery, options));
  };

  customElements.whenDefined('product-gallery').then(() => {
    document.addEventListener('gpoElementChanged', () => filterAllGalleries({ focusSelected: true }));

    // When the variant changes, the theme re-filters the gallery with the product media: we apply the Globo images again
    document.addEventListener('variant:change', () => filterAllGalleries());

    filterAllGalleries();
  });
})();
